import React from 'react';
import styled, { css, keyframes, createGlobalStyle } from 'styled-components';
import { getContext } from 'common';
import { NEUTRAL_GREY_300, KINGFISHER_BLUE_400 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/colors';
import { LABEL } from '@nokia-csf-uxr/freeform-design-tokens/tokens/typography';
import { NOKIA_PURE_TEXT } from '@nokia-csf-uxr/freeform-design-tokens/tokens/fontFamily';
import { FONT_SIZE_14 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/lineHeight';
import { TRANSITION_ACCELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';

import { OPACITY_12, OPACITY_24 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/opacity';
import { SHADOW_SPREAD_0, SHADOW_SPREAD_8 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/shadow_spread';
import { SPACING_8, SPACING_16, SPACING_24, SPACING_32 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import { RADIUS_2 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/border-radius';
import NokiaGridIconFont from '@nokia-csf-uxr/ccfk-assets/fonts/NokiaGridIconFont.woff';

import './generatedCss.css';

const CELL_RIPPLE = $theme => keyframes`
  0% {
    background-color: ${$theme.cell.active.focus.background};
    transform: scale(0) translateY(-50%);
  }

  50% {
    background-color: ${$theme.cell.active.pressed.background};
    transform: scale(1.2) translateY(-50%);
  }

  100% {
    background-color: ${$theme.cell.active.focus.background};
    transform: scale(1.2) translateY(-50%);
  }
`;
const Styled = {
  GridWrapper: styled.div`
    &.ag-theme-material-csf {
      ${({ $theme, masterDetail }) => css`
      & .ag-root-wrapper {
        background-color: ${$theme.cell.normal.background};
      }
      & .ag-cell-range-selected {
        border-bottom: 0.0625rem solid ${$theme.cell.normal.focus.border} !important;
        box-shadow: inset 0 -0.0625rem 0 0 ${$theme.cell.normal.focus.border} !important;
        background-color: ${$theme.cell.active.focus.background};
        border: none;
      }
      & .ag-cell-focus.ag-cell-range-single-cell {
        border-top: none !important;
        border-left: none !important;
        border-right: none !important;
      }
      & .ag-cell-focus:not(.ag-cell-range-selected):not(.ccfk-row-action-button) {
        border-bottom: 0.0625rem solid ${$theme.cell.normal.focus.border} !important;
        box-shadow: inset 0 -0.0625rem 0 0 ${$theme.cell.normal.focus.border};
      }

      // use this after pseudo class as cell focus bottom border for make the border sit on top of content of cell renderer
      & .ag-cell-focus:not(.ag-cell-range-selected):not(.ccfk-row-action-button)::after {
        content: '';
        width: 100%;
        height: 0.0625rem;
        position: absolute;
        bottom: 0;
        background-color: ${$theme.cell.normal.focus.border};
      }
      & .ag-cell-normal-height {
        height: 100%; 
      }

      // focused editable cell inner border effect
      // when ag-cell and ag-cell-value in different div. It means there is a cell renderer used.
      & .ag-row:not(.ag-row-selected) .ag-cell-focus:not(.ag-cell-range-selected) .ccfk-cell-editor-renderer-border,
      & .ag-cell:not(.ag-cell-range-selected):not(.ag-cell-inline-editing) .ccfk-cell-editor-renderer-border:hover {
        box-shadow: inset 0 0 0 0.0625rem ${$theme.cell.editable.hover.border};
        border-radius: ${RADIUS_2};
      }
      & .ag-row.ag-row-selected .ag-cell-focus:not(.ag-cell-range-selected):not(.ag-cell-inline-editing) .ccfk-cell-editor-renderer-border
       {
        box-shadow: inset 0 0 0 0.0625rem ${$theme.cell.editable.focus.border};
        border-radius: ${RADIUS_2};
      }

      
      // active editable cell inner border effect
      & .ag-cell-focus:not(.ag-cell-range-selected) .defaultCellEditor:not(:focus) {
        box-shadow: inset 0 0 0 0.0625rem ${$theme.cell.editable.focus.border};
        border-radius: ${RADIUS_2};
      }

      // header background style when columns resize
      &.ag-theme-material-csf .ag-header-group-cell:not(.ag-column-resizing) + .ag-header-group-cell:not(.ag-header-cell-moving):hover, .ag-theme-material-csf .ag-header-group-cell:not(.ag-column-resizing) + .ag-header-group-cell.ag-column-resizing,
      &.ag-theme-material-csf .ag-header-cell:not(.ag-column-resizing) + .ag-header-cell:not(.ag-header-cell-moving):hover,
      &.ag-theme-material-csf .ag-header-cell:not(.ag-column-resizing) + .ag-header-cell.ag-column-resizing,
      &.ag-theme-material-csf .ag-header-group-cell:first-of-type:not(.ag-header-cell-moving):hover,
      &.ag-theme-material-csf .ag-header-group-cell:first-of-type.ag-column-resizing,
      &.ag-theme-material-csf .ag-header-cell:first-of-type:not(.ag-header-cell-moving):hover,
      &.ag-theme-material-csf .ag-header-cell:first-of-type.ag-column-resizing {
        background-color: ${$theme.header.hover.background} !important; }

      // row background

      // css just for master detail grid.
      ${
  masterDetail
    ? `     & .ag-row-group-expanded:not(.ag-row-selected) {
              background-color: ${$theme.cell.active.background} !important;
              transition: background-color 300ms ${TRANSITION_ACCELERATE};
              box-shadow: -0.1875rem  0 0.1875rem  0 rgba(255,255,255,0.24), 0 0.1875rem  0.1875rem  0 rgba(0,0,0,0.24);
              z-index: 1;
            }
             & .ag-pinned-right-cols-container .ag-row-group-expanded:not(.ag-row-selected) {
              background-color: ${$theme.cell.active.background} !important;
              transition: background-color 300ms ${TRANSITION_ACCELERATE};
              box-shadow: 0.0625rem 0.1875rem 0.1875rem  0 rgba(0,0,0,0.24);
              z-index: 1;
            }
            & .ag-row[row-id*="detail"] {
              background-color: ${$theme.cell.normal.hover.background};
              border-bottom: 0.0625rem solid ${$theme.cell.normal.border};
            }
      
            & .ag-full-width-container {
              z-index: 0;
            }
            `
    : ''
}

      & .ag-row {
        background-color: ${$theme.cell.normal.background};
      }

      & .ag-row-hover:not(.ag-row-selected),
      & .ag-row-focus:not(.ag-row-selected) {
        background-color: ${$theme.cell.normal.hover.background};
      }

      // row selected background
      & .ag-row.ag-row-selected {
        background-color: ${$theme.cell.active.background};
        transition: background-color 300ms ${TRANSITION_ACCELERATE};
      }
      // row selected focus background
      & .ag-row-focus.ag-row-selected {
        background-color: ${$theme.cell.active.focus.background};
      }

      // row selected hover background
      & .ag-row-hover.ag-row-selected,
      & .ag-row-selected:hover {
        background-color: ${$theme.cell.active.hover.background};
      }

      & .ag-row.ag-row-focus.ag-row-selected:active {
        background-color: ${$theme.cell.active.pressed.background};
      }

      & .ag-header,
      & .ag-floating-top {
        border: none;
      }

      & .ag-header {
        background-color: ${$theme.header.background};
      }
      & .ag-header-row {
        border-bottom: 0.0625rem solid ${$theme.header.border};
      }

      & .ag-header-row:not(:first-child) .ag-header-cell, 
      & .ag-header-row:not(:first-child) .ag-header-group-cell.ag-header-group-cell-with-group {
        border-top: none !important;
      }

      & .ag-header-cell:hover, & .ag-header-group-cell:hover {
        background: ${$theme.header.hover.background} !important;
      }

      & .ag-header-row.ag-header-row-floating-filter .ag-header-cell:focus {
        background: ${$theme.header.focus.background};
      }

      & .ag-header-row.ag-header-row-column .ag-header-active:not(.ag-column-hover) {
        background: ${$theme.header.focus.background};
      }
      .ag-checkbox-input-wrapper::after {
        color: ${NEUTRAL_GREY_300};
      }

      .ag-checkbox-input-wrapper.ag-indeterminate::after,
      .ag-checkbox-input-wrapper.ag-checked::after {
        color: ${$theme.icon.color} !important;
      }

      & .ag-icon-filter:before,
      & .ag-icon-asc:before ,
      & .ag-icon-desc:before
      {
        color: ${$theme.cell.color};
      }

      & .ag-icon:not(.ag-icon-asc):not(.ag-icon-desc):not(.ag-icon-grip):before
      {
        opacity: 0.54;
      }

      // column header menu button hover bg color
      & .ag-header-cell-menu-button:hover {
        background-color: ${$theme.header.columnMenu.icon.hover.background};
      }
 
      .ag-row-selected .ag-cell.ag-cell-focus::before {
        animation: ${CELL_RIPPLE($theme)} 600ms ${TRANSITION_ACCELERATE};
        transform-origin: top;
        top: 50%;
      }
      .ag-cell::before {
        content: '';
        position: absolute;
        width: 100%;
        padding-top: 100%;
        left: 0;
        transform: scale(0);
        border-radius: 50%;
        z-index: -1;
      }

      .ag-cell:not(.ag-cell-range-single-cell),
      .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(.ag-cell-range-single-cell),
      .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left):not(.ag-cell-range-single-cell),
      .ag-full-width-row .ag-cell-wrapper.ag-row-group.ag-cell-expandable {
        border-bottom: 0.0625rem solid ${$theme.cell.normal.border};
      }


      & .ag-cell-focus.ccfk-row-action-button:not(.ag-cell-range-selected) {
        border-bottom: 0.0625rem solid ${$theme.cell.normal.border} !important;
      }

      & .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(.ag-cell-range-single-cell).ag-cell-focus,
      & .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left):not(.ag-cell-range-single-cell).ag-cell-focus {
        border-bottom: 0.0625rem solid ${$theme.cell.normal.focus.border};
      }

      // Todo: typography token

      & .ag-header-row  .ag-header-cell-text, .ag-header-row .ag-header-group-text {
        ${LABEL}
        color: ${$theme.header.color}
      }
      // text color of header cell when column is sorted
      & .ag-cell-label-container.ag-header-cell-sorted-asc *,
      & .ag-cell-label-container.ag-header-cell-sorted-desc * {
        color: ${$theme.header.filtered.color};
      }

      & .ag-cell-value,
      & .ag-group-value,
      & .ag-group-child-count {
        color: ${$theme.cell.color};
        font-family: ${NOKIA_PURE_TEXT};
        font-size: ${FONT_SIZE_14};
      }

      // Styles for Floating Filter
      .ag-floating-filter-button-button:focus:before {
        border: 0.125rem solid ${$theme.header.floatingFilter.border};
      }

      .ag-floating-filter.ag-focus-managed {
        caret-color: transparent;
      }

      .ag-floating-filter:after {
        height: inherit !important;
        width: inherit !important;
        background-color: ${$theme.header.focus.background} !important;
        transform: translate(-4px, -4px);
      }

      // Styling for Floating Filter: Input Box
      .cKfmfQ:focus {
        caret-color: black;
      }

      // styles for error state
      & .ag-root-wrapper.ccfk-cell-error {
        background-color: ${$theme.cell.error.normal.background};
      }
      & .ag-cell-range-selected.ccfk-cell-error {
        border-bottom: 0.0625rem solid ${$theme.cell.error.normal.focus.border} !important;
        box-shadow: inset 0 -0.0625rem 0 0 ${$theme.cell.error.normal.focus.border} !important;
      }
      & .ag-cell-focus:not(.ag-cell-range-selected).ccfk-cell-error:not(.ccfk-row-action-button) {
        border-bottom: 0.0625rem solid ${$theme.cell.error.normal.focus.border} !important;
        box-shadow: inset 0 -0.0625rem 0 0 ${$theme.cell.error.normal.focus.border};
      }
      & .ag-cell-focus:not(.ag-cell-range-selected).ag-cell-inline-editing.ccfk-cell-error:not(.ccfk-row-action-button) {
        background-color: ${$theme.cell.error.active.editing.background};
      }

      // use this after pseudo class as cell focus bottom border for make the border sit on top of content of cell renderer
      & .ag-cell-focus:not(.ag-cell-range-selected).ccfk-cell-error:not(.ccfk-row-action-button)::after {
        content: '';
        width: 100%;
        height: 0.0625rem;
        position: absolute;
        bottom: 0;
        background-color: ${$theme.cell.error.normal.focus.border};
      }

      // focused editable cell inner border effect
      // when ag-cell and ag-cell-value in different div. It means there is a cell renderer used.
      & .ag-row:not(.ag-row-selected).ag-cell-focus:not(.ag-cell-range-selected).ccfk-cell-error .ccfk-cell-editor-renderer-border,
      & .ag-cell:not(.ag-cell-range-selected).ag-cell-inline-editing.ccfk-cell-error .ccfk-cell-editor-renderer-border:focus {
        box-shadow: inset 0 0 0 0.0625rem ${$theme.cell.error.editable.hover.border};
        border-radius: ${RADIUS_2};
      }
      & .ag-row:not(.ag-row-selected) .ccfk-cell-error:not(.ag-cell-focus):not(.ag-cell-range-selected) .ccfk-cell-editor-renderer-border,
      & .ag-cell:not(.ag-cell-range-selected) .ccfk-cell-error:not(.ag-cell-inline-editing) .ccfk-cell-editor-renderer-border:hover {
        box-shadow: inset 0 0 0 0.0625rem ${$theme.cell.error.editable.hover.border};
        border-radius: ${RADIUS_2};
      }
      & .ag-cell:not(.ag-cell-range-selected):not(.ag-cell-inline-editing).ccfk-cell-error .ccfk-cell-editor-renderer-border:hover {
        box-shadow: inset 0 0 0 0.0625rem ${$theme.cell.error.editable.hover.border};
        border-radius: ${RADIUS_2};
      }
      & .ag-row.ag-row-selected .ag-cell-focus:not(.ag-cell-range-selected):not(.ag-cell-inline-editing).ccfk-cell-error .ccfk-cell-editor-renderer-border
       {
        box-shadow: inset 0 0 0 0.0625rem ${$theme.cell.error.editable.focus.border};
        border-radius: ${RADIUS_2};
      }


      // active editable cell inner border effect
      & .ag-cell-focus:not(.ag-cell-range-selected).ccfk-cell-error .defaultCellEditor:not(:focus) {
        box-shadow: inset 0 0 0 0.0625rem ${$theme.cell.error.editable.focus.border};
      }
      // active editable cell inner border effect
      & .ag-cell-focus:not(.ag-cell-range-selected).ccfk-cell-error .defaultCellEditor:focus {
        box-shadow: inset 0 0 0 0.125rem ${$theme.cell.error.editable.focus.border};
      }

      // row background

      // css just for master detail grid.
      ${
  masterDetail
    ? `     & .ag-row-group-expanded:not(.ag-row-selected).ccfk-cell-error {
              background-color: ${$theme.cell.error.active.background} !important;
              transition: background-color 300ms ${TRANSITION_ACCELERATE};
              box-shadow: -0.1875rem  0 0.1875rem  0 rgba(255,255,255,${OPACITY_24}), 0 0.1875rem  0.1875rem  0 rgba(0,0,0,{OPACITY_24});
              z-index: 1;
            }
             & .ag-pinned-right-cols-container .ag-row-group-expanded:not(.ag-row-selected).ccfk-cell-error {
              background-color: ${$theme.cell.error.active.background} !important;
              transition: background-color 300ms ${TRANSITION_ACCELERATE};
              box-shadow: 0.0625rem 0.1875rem 0.1875rem  0 rgba(0,0,0,0.24);
              z-index: 1;
            }
            & .ag-row[row-id*="detail"].ccfk-cell-error {
              background-color: ${$theme.cell.error.normal.hover.background};
              border-bottom: 0.0625rem solid ${$theme.cell.error.normal.border};
            }
      
            & .ag-full-width-container.ccfk-cell-error {
              z-index: 0;
            }
            `
    : ''
}

      & .ag-row .ccfk-row-error
      & .ag-row .ccfk-cell-error {
        background-color: ${$theme.cell.error.normal.background};
      }

      // row selected background
      & .ag-row.ag-row-selected .ccfk-row-error,
      & .ag-row.ag-row-selected .ccfk-cell-error {
        background-color: ${$theme.cell.error.active.background};
        transition: background-color 300ms ${TRANSITION_ACCELERATE};
      }
      // row selected focus background
      & .ag-row-focus.ag-row-selected .ccfk-row-error,
      & .ag-row-focus.ag-row-selected .ccfk-cell-error {
        background-color: ${$theme.cell.error.active.focus.background};
      }

      // row selected hover background
      & .ag-row-hover.ag-row-selected .ccfk-row-error,
      & .ag-row-selected:hover .ccfk-row-error,
      & .ag-row-hover.ag-row-selected .ccfk-cell-error,
      & .ag-row-selected:hover .ccfk-cell-error {
        background-color: ${$theme.cell.error.active.hover.background};
      }

      & .ag-row.ag-row-focus.ag-row-selected:active .ccfk-row-error,
      & .ag-row.ag-row-focus.ag-row-selected:active .ccfk-cell-error {
        background-color: ${$theme.cell.error.active.pressed.background};
      }

      .ag-cell:not(.ag-cell-range-single-cell) .ccfk-cell-error,
      .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right) .ccfk-cell-error:not(.ag-cell-range-single-cell),
      .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left) .ccfk-cell-error:not(.ag-cell-range-single-cell) .ccfk-cell-error,
      .ag-full-width-row .ag-cell-wrapper.ag-row-group.ag-cell-expandable .ccfk-cell-error {
        border-bottom: 0.0625rem solid ${$theme.cell.error.normal.border};
      }

      & .ag-cell-focus .ccfk-cell-error.ccfk-row-action-button:not(.ag-cell-range-selected){
        border-bottom: 0.0625rem solid ${$theme.cell.error.normal.border} !important;
      }

      & .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right) .ccfk-cell-error:not(.ag-cell-range-single-cell).ag-cell-focus,
      & .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left) .ccfk-cell-error:not(.ag-cell-range-single-cell).ag-cell-focus {
        border-bottom: 0.0625rem solid ${$theme.cell.error.normal.focus.border};
      }

      // styles for the whole row in the error state
      & .ag-row.ccfk-row-error,
      & .ag-row.ccfk-cell-error {
        background-color: ${$theme.cell.error.normal.background};
      }

      & .ag-row-hover:not(.ag-row-selected).ccfk-row-error,
      & .ag-row-focus:not(.ag-row-selected).ccfk-row-error,
      & .ag-row-hover:not(.ag-row-selected).ccfk-cell-error,
      & .ag-row-focus:not(.ag-row-selected).ccfk-cell-error {
        background-color: ${$theme.cell.error.normal.hover.background};
      }

      // row selected background
      & .ag-row.ag-row-selected.ccfk-row-error,
      & .ag-row.ag-row-selected.ccfk-cell-error {
        background-color: ${$theme.cell.error.active.background};
        transition: background-color 300ms ${TRANSITION_ACCELERATE};
      }

      // row selected focus background
      & .ag-row-focus.ag-row-selected.ccfk-row-error,
      & .ag-row-focus.ag-row-selected.ccfk-cell-error {
        background-color: ${$theme.cell.error.active.focus.background};
      }

      // row selected hover background
      & .ag-row-hover.ag-row-selected.ccfk-row-error,
      & .ag-row-selected:hover.ccfk-row-error,
      & .ag-row-hover.ag-row-selected.ccfk-cell-error,
      & .ag-row-selected:hover.ccfk-cell-error {
        background-color: ${$theme.cell.error.active.hover.background};
      }

      & .ag-row.ag-row-focus.ag-row-selected:active.ccfk-row-error,
      & .ag-row.ag-row-focus.ag-row-selected:active.ccfk-cell-error {
        background-color: ${$theme.cell.error.active.background};
      }

      .ag-cell:not(.ag-cell-range-single-cell).ccfk-row-error,
      .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right).ccfk-row-error:not(.ag-cell-range-single-cell),
      .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left).ccfk-row-error:not(.ag-cell-range-single-cell) .ccfk-cell-error,
      .ag-full-width-row .ag-cell-wrapper.ag-row-group.ag-cell-expandable.ccfk-row-error {
        border-bottom: 0.0625rem solid ${$theme.cell.error.normal.border};
      }

      & .ag-cell-focus.ccfk-row-error.ccfk-row-action-button:not(.ag-cell-range-selected){
        border-bottom: 0.0625rem solid ${$theme.cell.error.normal.border} !important;
      }

      & .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right).ccfk-row-error:not(.ag-cell-range-single-cell).ag-cell-focus,
      & .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left).ccfk-row-error:not(.ag-cell-range-single-cell).ag-cell-focus {
        border-bottom: 0.0625rem solid ${$theme.cell.error.normal.focus.border};
      }
    `}
    }
  `
};

/** Below is global styles for DataGrid */

// Copied box-shadow of List from List/styles/index.js
const SHADOW_1 = `${SHADOW_SPREAD_0} ${SHADOW_SPREAD_8} ${SHADOW_SPREAD_8} ${SHADOW_SPREAD_0} rgba(0, 0, 0, ${OPACITY_24})`;
const SHADOW_2 = `${SHADOW_SPREAD_0} ${SHADOW_SPREAD_0} ${SHADOW_SPREAD_8} ${SHADOW_SPREAD_0} rgba(0, 0, 0, ${OPACITY_12})`;

// Because row dragging floating panel is attached directly to body tag
// and column menu could be attached to body tag via popupParent property,
// all the custom style for them cannot be scoped by placing them in DataGrid/styles/index.js.
// Put these styles here to make them to be accessible globally.

/**
 * Part of style in generatedCSS is scoped because of Theme.scss.
 * Use the code below for separate them and scope them separately.
 * */


const GlobalCCFKGridStyle = createGlobalStyle`
@font-face {
  font-family: 'Nokia DataGrid Icon Font';
  src: url('${NokiaGridIconFont}') format('woff');
  font-weight: normal;
  font-style: normal;
}

.ccfk-DataGrid-tabular-number-column {
  font-variant-numeric: tabular-nums; 
}

// .ag-dnd-ghost is in same div element with .ag-theme-material-csf, so scope it here separately.
.ag-theme-material-csf.ag-dnd-ghost {
  position: absolute;
  display: inline-flex;
  align-items: center;
  cursor: move;
  white-space: nowrap; 
}

${({ $theme }) => css`
  .ag-theme-material-csf .ag-menu {
    background-color: ${$theme.header.columnMenu.background};
  }

  .ag-theme-material-csf.ag-dnd-ghost {
    color: ${$theme.rowDragFloatingCell.content};
    height: 40px !important;
    background: ${$theme.rowDragFloatingCell.background};
    border: solid 0.0625rem ${$theme.rowDragFloatingCell.border} !important;
    box-shadow: 0 0 ${$theme.rowDragFloatingCell.shadowSpread} 0 ${$theme.rowDragFloatingCell.shadowColorOne},
    0 ${$theme.rowDragFloatingCell.shadowSpread} ${$theme.rowDragFloatingCell.shadowSpread} 0 ${$theme.rowDragFloatingCell.shadowColorTwo};
  }

  .ag-theme-material-csf {
    // color for:
    // 1) column menu tabs,
    // 2) input filed color and
    // in column menu
    .ag-tab-selected {
      border-bottom-color: ${$theme.cell.normal.focus.border} !important;
    }
    .ag-icon:before {
      color: ${$theme.cell.color};
    }

    .ag-icon-grip::before {
      font-size: 1.125rem;
      color: ${$theme.icon.dragIcon.color};
    }
    .ag-row-dragging-icon-window::before {
      color: ${$theme.cell.color};
    }

    input:focus[class^='ag-'][type='text'] {
      border-bottom-color: ${$theme.cell.normal.focus.border} !important;
    }

    .ag-select .ag-picker-field-wrapper {
      background-color: ${$theme.input.background};
    }

    .ag-select .ag-picker-field-wrapper * {
      color: ${$theme.input.color};
    }

    .ag-picker-field-wrapper {
      border-radius: 0 !important;
      padding: 0.25rem 0.625rem;
      outline: none;
    }

    .ag-picker-field-wrapper:focus {
      box-shadow: inset 0px 0px 0px 0.125rem ${$theme.cell.normal.focus.border};
      border-color: transparent;
    }

    .ag-picker-field-wrapper:hover:not(:focus) {
      box-shadow: inset 0px 0px 0px 0.125rem #3d3d3d;
      border-color: transparent;
    }

    .ag-select-list {
      background-color: ${$theme.input.background};
      box-shadow: ${SHADOW_1}, ${SHADOW_2};
    }

    .ag-select-list .ag-select-list-item {
      color: ${$theme.cell.color};
    }
    .ag-ltr .ag-select-list .ag-select-list-item {
      padding: 0 ${SPACING_8} 0 ${SPACING_16};
    }
    .ag-rtl .ag-select-list .ag-select-list-item {
      padding: 0 ${SPACING_16} 0 ${SPACING_8};
    }

    .ag-list-item.ag-select-list-item.ag-active-item {
      background-color: ${$theme.cell.normal.hover.background};
    }
    .ag-select-list-item.ag-list-item:focus:not(:hover),
    .ag-list-item.ag-select-list-item.ag-active-item:active {
      background-color: ${$theme.cell.normal.hover.background};
      box-shadow: inset 0 0 0 2px ${KINGFISHER_BLUE_400}; // LIST_FOCUS, PRIMARY_GLOBAL_FOCUS
    }

    input[class^='ag-']:not([type]),
    input[class^='ag-'][type='text'],
    input[class^='ag-'][type='number'],
    input[class^='ag-'][type='tel'],
    input[class^='ag-'][type='date'],
    input[class^='ag-'][type='datetime-local'],
    textarea[class^='ag-'] {
      color: ${$theme.input.color};
    }

    input[class^='ag-']:not([type])::placeholder,
    input[class^='ag-'][type='text']::placeholder,
    input[class^='ag-'][type='number']::placeholder,
    input[class^='ag-'][type='tel']::placeholder,
    input[class^='ag-'][type='date']::placeholder,
    input[class^='ag-'][type='datetime-local']::placeholder,
    textarea[class^='ag-'] {
      color: ${$theme.input.color};
    }

    .ag-input-field-label.ag-label.ag-radio-button-label {
      color: ${$theme.input.color};
    }

    .ag-radio-button-input-wrapper::after,
    .ag-radio-button-input-wrapper.ag-checked::after {
      color: ${$theme.icon.color};
    }

    .ag-menu {
      background-color: ${$theme.header.columnMenu.background};
    }

    .ag-menu-option {
      height: ${SPACING_32};
      justify-content: flex-start;
    }

    .ag-ltr .ag-menu-option {
      padding: 0 ${SPACING_8} 0 ${SPACING_16};
    }

    .ag-rtl .ag-menu-option {
      padding: 0 ${SPACING_16} 0 ${SPACING_8};
    }

    .ag-menu-option,
    .ag-menu-separator {
      display: flex;
      justify-content: space-between;
    }

    .ag-menu-separator {
      height: 0.0625rem !important;
      margin: 0;
    }

    .ag-menu-separator > div {
      width: 100%;
    }

    .ag-set-filter-item {
      margin: 0px ${SPACING_16};
    }

    .ag-menu-option-icon,
    .ag-compact-menu-option-icon {
      width: 0px;
    }
    .ag-menu-option-part,
    .ag-compact-menu-option-part {
      line-height: 16px;
      height: ${SPACING_32};
    }

    .ag-menu-option-part {
      padding: ${SPACING_8} 0;
    }

    .ag-menu-header {
      background-color: ${$theme.header.background};
    }

    .ag-menu .ag-label,
    .ag-menu .ag-menu-option-text,
    .ag-menu .ag-menu-option-shortcut {
      color: ${$theme.cell.color};
    }

    .ag-menu-option-active,
    .ag-compact-menu-option-active {
      background-color: ${$theme.cell.normal.hover.background};
    }

    .ag-menu-option-part.ag-menu-option-text {
      padding: ${SPACING_8} 0;
      flex: 1;
    }
    .ag-menu-option-part.ag-menu-option-shortcut {
      padding-right: 0;
    }

    .ag-ltr .ag-menu-option-icon {
      padding-left: 0 !important;
      padding-right: 1.75rem;
    }

    .ag-rtl .ag-menu-option-icon {
      padding-right: 0 !important;
      padding-left: 1.75rem;
    }

    .ag-ltr .ag-menu-option-popup-pointer {
      padding-left: ${SPACING_24};
    }

    .ag-rtl .ag-menu-option-popup-pointer {
      padding-right: ${SPACING_24};
    }

    // column menu checkbox
    .ag-checkbox-input-wrapper.ag-indeterminate::after,
    .ag-checkbox-input-wrapper.ag-checked::after {
      color: ${$theme.icon.color} !important;
    }

    // color for column menu tabs in column menu
    .ag-keyboard-focus .ag-virtual-list-item:focus:after {
      border-color: ${$theme.cell.normal.focus.border} !important;
    }

    .ag-keyboard-focus .ag-tab:focus:after {
      color: ${$theme.cell.normal.focus.border};
      border-color: ${$theme.cell.normal.focus.border};
    }

    .ag-filter-no-matches {
      color: ${$theme.cell.color};
    }
  }
`}
`;

const ThemedGlobalCCFKGridStyle = () => getContext(({ theme }) => <GlobalCCFKGridStyle $theme={theme.DATAGRID} />);
ThemedGlobalCCFKGridStyle.displayName = 'GlobalCCFKGridStyle';

export { Styled as default, ThemedGlobalCCFKGridStyle };
